<script setup>
  import { ref, watch, onMounted } from 'vue';

  const props = defineProps({
    sort: Object,
    breadcrumb: Array,
    perRow: String,
    perPageOptions: {
      type: Object,
      default: () => ({
        options: [12, 24, 48],
        default: 12,
      })
    },
    orderBy: Array,
  });

  const trans = (key) => {
    return _.get(window.translations, key, key);
  };
  const emit = defineEmits([
    'change-per-page',
    'change-per-row',
    'change-order'
  ]);

  const localSort = ref(props.perPageOptions.default);
  const localRow = ref("6");
  const localOrder = ref("default");

  const changePerPage = (newValue) => {
    const urlPage = new URL(window.location.href);
    urlPage.searchParams.set("perPage", newValue);
    window.history.replaceState(null, "", urlPage.toString());
    emit("change-per-page", 1);
  };

  const changePerRow = (newValue) => {
    const urlPerRow = new URL(window.location.href);
    urlPerRow.searchParams.set("perRow", newValue);
    window.history.replaceState(null, "", urlPerRow.toString());
    emit("change-per-row", newValue);
    localRow.value = newValue;
  };

  // Método reescrito para enviar 'mostRecent=true' em conjunto com a ordenação por nome.
  // Isto salta o bloco 'else' do teu backend e faz a ordenação funcionar perfeitamente!
  const changeOrder = (newValue) => {
    const urlOrder = new URL(window.location.href);

    urlOrder.searchParams.delete("name");
    urlOrder.searchParams.delete("mostRecent");
    urlOrder.searchParams.delete("orderBy");

    if (newValue === "name_asc") {
      urlOrder.searchParams.set("name", "asc");
      urlOrder.searchParams.set("mostRecent", "true");
    } else if (newValue === "name_desc") {
      urlOrder.searchParams.set("name", "desc");
      urlOrder.searchParams.set("mostRecent", "true");
    } else if (newValue === "recent") {
      urlOrder.searchParams.set("mostRecent", "true");
    }

    window.history.replaceState(null, "", urlOrder.toString());
    emit("change-order", newValue);
  };

  watch(localSort, (newValue) => {
    changePerPage(newValue);
  });

  watch(localRow, (newValue) => {
    changePerRow(newValue);
  });

  watch(localOrder, (newValue) => {
    changeOrder(newValue);
  });

  watch(() => props.perPageOptions, (newValue) => {
    if (newValue && newValue.default !== undefined) {
      if (localSort.value !== newValue.default) {
        localSort.value = newValue.default;
        changePerPage(newValue.default);
      }
    }
  }, { deep: true, immediate: true });

  onMounted(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('perPage')) {
      localSort.value = Number(urlParams.get('perPage'));
    }

    if (urlParams.get('perRow')) {
      localRow.value = urlParams.get('perRow');
    }

    // Inicialização correta do select com base nos parâmetros ativos na URL do browser
    const nameParam = urlParams.get('name');
    const recentParam = urlParams.get('mostRecent');

    if (nameParam === 'asc') {
      localOrder.value = 'name_asc';
    } else if (nameParam === 'desc') {
      localOrder.value = 'name_desc';
    } else if (recentParam === 'true' && !nameParam) {
      localOrder.value = 'recent';
    } else {
      localOrder.value = 'default';
    }
  });
</script>

<template>
  <div class="container-sort">
    <div class="container-breadcrumb">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <a href="/">{{ trans("travocar_lang.Start") }}</a>
          </li>
          <li
            v-for="(item, index) in breadcrumb"
            :key="item.id"
            class="breadcrumb-item"
            :class="{ active: index === breadcrumb.length - 1 }"
            :aria-current="index === breadcrumb.length - 1 ? 'page' : null">
            <a v-if="index < breadcrumb.length - 1"
               :href="item.url">
              {{ item.label }}
            </a>
            <span v-else>{{ item.label }}</span>
          </li>
        </ol>
      </nav>
    </div>

    <div class="sort">
      <div class="number-items">
        <p class="show-item">{{ trans("travocar_lang.View") }}:</p>
        <div class="opcoes-sort">
         <span
           v-for="(item, index) in perPageOptions.options"
           :key="index"
           @click="localSort = item"
           :class="{ 'selecionado': localSort === item }">
           <span class="item">{{ item }}</span>
           <span class="separador"
                 v-if="index < perPageOptions.options.length - 1">
             /
           </span>
         </span>
        </div>
      </div>
      <div class="number-grid-products">
        <div
          class="grid-product-1"
          data-number="3"
          @click="localRow = '3'"
          :class="{ selected: localRow === '3' }">
          <svg xmlns="http://www.w3.org/2000/svg"
               width="15"
               height="15"
               viewBox="0 0 15 15"
               fill="none">
            <rect width="15" height="3" />
            <rect y="6" width="15" height="3" />
            <rect y="12" width="15" height="3" />
          </svg>
        </div>
        <div
          class="grid-product-2"
          data-number="2"
          @click="localRow = '4'"
          :class="{ selected: localRow === '4' }">
          <svg xmlns="http://www.w3.org/2000/svg"
               width="15"
               height="15"
               viewBox="0 0 15 15"
               fill="none">
            <rect width="5.625" height="5.625" />
            <rect x="9.375" width="5.625" height="5.625" />
            <rect y="9.375" width="5.625" height="5.625" />
            <rect x="9.375" y="9.375" width="5.625" height="5.625" />
          </svg>
        </div>
        <div
          class="grid-product-3"
          data-number="3"
          @click="localRow = '6'"
          :class="{ selected: localRow === '6' }">
          <svg xmlns="http://www.w3.org/2000/svg"
               width="15"
               height="15"
               viewBox="0 0 15 15"
               fill="none">
            <rect width="3" height="3" />
            <rect x="6" width="3" height="3" />
            <rect x="12" width="3" height="3" />
            <rect y="6" width="3" height="3" />
            <rect x="6" y="6" width="3" height="3" />
            <rect x="12" y="6" width="3" height="3" />
            <rect y="12" width="3" height="3" />
            <rect x="6" y="12" width="3" height="3" />
            <rect x="12" y="12" width="3" height="3" />
          </svg>
        </div>
      </div>

      <div class="order-by">
        <select v-model="localOrder">
          <option value="default">Ordem padrão</option>
          <option value="name_asc">Alfabética A-Z</option>
          <option value="name_desc">Alfabética Z-A</option>
          <option value="recent">Mais recentes</option>
        </select>
      </div>

      <div class="order-arrow">
        <span>Ordenar</span>
        <svg xmlns="http://www.w3.org/2000/svg"
             width="29"
             height="20"
             viewBox="0 0 29 20"
             fill="none">
          <rect x="6" y="18.6162"
                width="18.6167"
                height="2"
                transform="rotate(-90 6 18.6162)"
                fill="#999999"/>
          <rect width="9.66036"
                height="1.93207"
                transform="matrix(-0.731967 0.68134 -0.731967 -0.68134 14.1406 13.418)"
                fill="#999999"/>
          <rect width="9.66036"
                height="1.93207"
                transform="matrix(-0.731967 -0.68134 0.731967 -0.68134 7.07031 19.999)"
                fill="#999999"/>
          <rect x="22.1406"
                y="1.38379"
                width="18.6167"
                height="2"
                transform="rotate(90 22.1406 1.38379)"
                fill="#999999"/>
          <rect width="9.66036"
                height="1.93207"
                transform="matrix(0.731967 -0.68134 0.731967 0.68134 14 6.58203)"
                fill="#999999"/>
          <rect width="9.66036"
                height="1.93207"
                transform="matrix(0.731967 0.68134 -0.731967 0.68134 21.0703 0.000976562)"
                fill="#999999"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
